import { createContext, useContext, useEffect } from "react";
import { SITE } from "@/data/site";

export const SeoContext = createContext(null);

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Manages the document <head> for the current route (client-side, crawlable by Google).
 * props: title, description, path (relative, e.g. "/faq"), image, schema (object|array), noindex
 */
export default function Seo({ title, description, path = "/", image, schema, noindex = false }) {
  const collect = useContext(SeoContext);
  if (collect) collect({ title, description, path, image, schema, noindex });
  useEffect(() => {
    const url = `${SITE.url}${path}`;
    const img = image || SITE.ogImage;
    document.title = title;
    document.documentElement.lang = "en-US";

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
    if (noindex) document.head.querySelector('link[rel="canonical"]')?.remove();
    else upsertLink("canonical", url);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:locale", "en_US");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // JSON-LD
    const prev = document.head.querySelectorAll('script[data-seo-jsonld="1"]');
    prev.forEach((n) => n.remove());
    if (schema) {
      const list = Array.isArray(schema) ? schema : [schema];
      list.forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-jsonld", "1");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }

  }, [title, description, path, image, schema, noindex]);

  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [path]);

  return null;
}
